import axios from 'axios';
import { withFreshAuth } from './freshAuth';
import { ENDPOINTS } from '../config/endpoints';

const API_BASE_URL = ENDPOINTS.history;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

api.interceptors.request.use(withFreshAuth);

// Текст ошибки папок для человека: коды сервера (409 folder_exists и т. п.)
// наружу не выходят.
function folderError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 409 && detail === 'folder_exists') return new Error('Папка с таким названием уже есть');
  if (status === 409 && detail === 'folders_limit') return new Error('Папок не больше 100');
  if (status === 400) return new Error(typeof detail === 'string' && detail ? detail : 'Название — от 1 до 64 знаков');
  return new Error('Не получилось. Попробуйте ещё раз');
}

export const historyService = {
  // Завершитель (bit-history/app/jobs.py): задача, зарегистрированная сразу
  // после старта, доедет до Истории и с закрытой вкладкой.
  async registerJob({ taskId, service, title, meta }) {
    const response = await api.post('/jobs', { task_id: taskId, service, title, meta: meta || null });
    return response.data; // { id }
  },

  async listJobs() {
    const response = await api.get('/jobs');
    return response.data.jobs; // [{ id, task_id, service, title, status, item_id, last_error, seen_at, … }]
  },

  async markJobSeen(id) {
    const response = await api.post(`/jobs/${id}/seen`);
    return response.data;
  },

  async list({ q, ...params } = {}) {
    // С непустым q ручка ищет (limit ≤ 50, у записей поле match); пустой q не
    // шлём вовсе — это обычный список.
    const query = (q || '').trim();
    if (query) params.q = query;
    const response = await api.get('/items', { params });
    return response.data; // { items: [...], total }
  },

  // Поиск по смыслу (bit-history GET /search/semantic): своя запись, время места
  // в разговоре (`at`) или признак протокола и цитата. Отказ (503 — поиск выключен
  // или индекс недоступен) вызывающий проглатывает: остаётся обычный поиск.
  async semantic(q, limit = 8) {
    const response = await api.get('/search/semantic', { params: { q, limit } });
    return response.data.items || [];
  },

  // Папки (один уровень, личные). 404 — функция для этой учётки выключена.
  async listFolders() {
    try {
      const response = await api.get('/folders');
      return response.data; // { folders: [{ id, name, count }], max }
    } catch (err) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async createFolder(name) {
    try {
      const response = await api.post('/folders', { name });
      return response.data; // { id, name }
    } catch (err) {
      throw folderError(err);
    }
  },

  async renameFolder(id, name) {
    try {
      const response = await api.patch(`/folders/${id}`, { name });
      return response.data; // { id, name }
    } catch (err) {
      throw folderError(err);
    }
  },

  async deleteFolder(id) {
    const response = await api.delete(`/folders/${id}`);
    return response.data; // { status, detached } — записи остаются в Истории
  },

  async get(id) {
    const response = await api.get(`/items/${id}`);
    return response.data;
  },

  async save(item) {
    // item: { type, title, transcript, summary?, meta? }
    const response = await api.post('/items', item);
    return response.data; // { id }
  },

  async patch(id, fields) {
    // fields: { title?, summary?, selected_summary_id? }
    // ВНИМАНИЕ: summary ЗАМЕНЯЕТ основной протокол записи, а не добавляет ещё
    // один — этим путём ходят «Подробнее» и «Короче». Новый протокол рядом с
    // прежним — addSummary.
    const response = await api.patch(`/items/${id}`, fields);
    return response.data;
  },

  async addSummary(id, summary) {
    // summary: { template, model, text }. Новый становится основным.
    const response = await api.post(`/items/${id}/summaries`, summary);
    return response.data; // { id }
  },

  async removeSummary(id, summaryId) {
    const response = await api.delete(`/items/${id}/summaries/${summaryId}`);
    return response.data;
  },

  async selectSummary(id, summaryId) {
    // Основной открывается первым и уходит по публичной ссылке.
    const response = await api.patch(`/items/${id}`, { selected_summary_id: summaryId });
    return response.data;
  },

  async remove(id) {
    const response = await api.delete(`/items/${id}`);
    return response.data;
  },

  async removeAll() {
    const response = await api.delete('/items');
    return response.data; // { deleted: N }
  },

  async createShare(id, { includes, ttl }) {
    // includes: ['transcript','protocol'], ttl: '7d'|'30d'|'forever'
    const response = await api.post(`/items/${id}/share`, { includes, ttl });
    return response.data; // { token, includes, expires_at, url }
  },

  async revokeShare(id) {
    const response = await api.delete(`/items/${id}/share`);
    return response.data; // { status: 'revoked' }
  },

  // Копия поделённой записи в свою Историю. Повтор той же ссылки отдаёт
  // прежнюю копию, а не вторую (bit-history, POST /shared/{token}/save).
  async saveShared(token) {
    const response = await api.post(`/shared/${token}/save`);
    return response.data; // { id }
  },

  // `view` — это открытие страницы ссылки человеком: bit-history засчитает его
  // автору («Открыли N раз»). Ставит только PublicSharePage, раз на вкладку.
  async getShared(token, { view = false } = {}) {
    // Публичный эндпоинт — БЕЗ token-заголовка (отдельный axios, минуя интерсептор).
    const response = await axios.get(`${API_BASE_URL}/shared/${token}`, {
      headers: { Accept: 'application/json' },
      ...(view ? { params: { view: 1 } } : {}),
    });
    return response.data;
  },

  // Мои ссылки числом: { live, views, last_view_at } — для личных метрик.
  async myShares() {
    const response = await api.get('/me/shares');
    return response.data;
  },
};
